import { useState } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/ui';
import { ApiError } from '@shared/api';
import { ALLOWED_CONTENT_TYPES, type MediaLink } from '@entities/media';
import { MediaPicker } from '@features/media-picker';
import {
  useAttachOrderDocumentMutation,
  useDetachOrderDocumentMutation,
  type OrderView,
} from '@entities/order';

const DOC_TYPES = [...ALLOWED_CONTENT_TYPES.document, ...ALLOWED_CONTENT_TYPES.image];

export function OrderDocuments({ order }: { order: OrderView }) {
  const { t } = useTranslation();
  const attach = useAttachOrderDocumentMutation(order.id);
  const detach = useDetachOrderDocumentMutation(order.id);
  const [error, setError] = useState<string | null>(null);
  const [pendingMediaId, setPendingMediaId] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-4">
      {error ? <Alert type="error" showIcon message={error} /> : null}

      {order.documents.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {order.documents.map((link) => (
            <DocumentRow
              key={link.id}
              link={link}
              busy={detach.isPending && detach.variables === link.id}
              onRemove={() => {
                setError(null);
                detach.mutate(link.id, {
                  onError: (err) =>
                    setError(err instanceof ApiError ? err.message : 'Remove failed'),
                });
              }}
            />
          ))}
        </ul>
      ) : (
        <p className="text-sm opacity-70">{t('orders.documents.empty')}</p>
      )}

      <MediaPicker
        scope="ORDER_DOCUMENT"
        label={t('orders.documents.attach')}
        hint={t('orders.documents.attachHint')}
        accept={DOC_TYPES}
        multiple={false}
        value={pendingMediaId ? [pendingMediaId] : []}
        onChange={(ids) => {
          const next = ids[0];
          if (!next) {
            setPendingMediaId(null);
            return;
          }
          setPendingMediaId(next);
          setError(null);
          attach.mutate(next, {
            onSuccess: () => setPendingMediaId(null),
            onError: (err) => {
              setPendingMediaId(null);
              setError(err instanceof ApiError ? err.message : 'Attach failed');
            },
          });
        }}
      />
    </div>
  );
}

function DocumentRow({
  link,
  busy,
  onRemove,
}: {
  link: MediaLink;
  busy: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm">
      <span className="truncate font-mono text-xs">{link.id.slice(0, 12)}</span>
      <div className="flex gap-2">
        {link.url ? (
          <a
            href={link.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary underline-offset-2 hover:underline"
          >
            {t('orders.documents.open')}
          </a>
        ) : null}
        <Button type="button" size="sm" variant="ghost" loading={busy} onClick={onRemove}>
          {t('common.remove')}
        </Button>
      </div>
    </li>
  );
}
